// JSZip se carga vía dynamic import dentro de downloadFilesAsZip para
// que no entre en el main bundle. Round 2026-04-29 (F2) — JSZip son
// ~95 KB minificados que solo se usan al descargar adjuntos en bulk.

import { MENSAJES_DESCARGA, estadoDeStatus } from "./descargaArchivo";

/**
 * Helpers para descargar archivos del storage del backend.
 *
 * Las URLs vienen firmadas con JWT en el path (`/files/<token>`) — son
 * GET autenticadas vía el JWT del path. El interceptor global de
 * `window.fetch` agrega `credentials: "include"` para que la cookie
 * HttpOnly también viaje (round 2026-04-26 fase 6 — P1-B). Aún así
 * fetcheamos el blob nosotros mismos para:
 *
 *   1) poder forzar el `download` attribute (los browsers lo ignoran si
 *      la URL es cross-origin sin Content-Disposition adecuado).
 *   2) extraer la extensión del MIME type cuando no tenemos el nombre
 *      original (las URLs firmadas no llevan filename legible).
 *   3) poder armar un ZIP de varios archivos sin abrir tabs nuevas.
 */

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "video/x-msvideo": "avi",
  "video/x-matroska": "mkv",
  "application/pdf": "pdf",
};

const inferExtension = (mime: string, fallbackUrl: string): string => {
  if (MIME_TO_EXT[mime]) return MIME_TO_EXT[mime];
  // último recurso: si el path contiene una extensión legible, usarla
  const m = fallbackUrl.match(/\.([a-zA-Z0-9]{2,5})(\?|$)/);
  return m ? m[1].toLowerCase() : "bin";
};

const sanitizeName = (name: string): string =>
  name.replace(/[\\/:*?"<>|]+/g, "_").trim() || "archivo";

/** Descarga un blob desde la URL firmada. La auth viaja en el JWT del path
 *  (`/files/<JWT>`) y/o vía cookie HttpOnly (incluida automáticamente por
 *  el interceptor de `window.fetch`). */
const fetchBlob = async (url: string): Promise<Blob> => {
  const res = await fetch(url);
  if (!res.ok) {
    // Round 2026-10-28: el mensaje llega tal cual a un toast. «Error descargando archivo (HTTP 401)» no le dice
    // nada a quien descarga; el token firmado vencido y el archivo borrado tienen aviso propio. Un 401 de /files
    // ya NO cierra la sesión (interceptor de apiClient.ts), así que este aviso es lo único que ve la persona.
    const estado = estadoDeStatus(res.status);
    throw new Error(
      estado === "ok" ? `Error descargando archivo (HTTP ${res.status})` : MENSAJES_DESCARGA[estado],
    );
  }
  return res.blob();
};

/** Trigger del download nativo del browser para un blob. */
const triggerDownload = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = sanitizeName(filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Liberamos en el siguiente tick para que el browser ya haya iniciado el download
  setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
};

/**
 * Descarga un solo archivo. Si `suggestedName` viene sin extensión, se
 * agrega la extensión inferida del MIME type del blob.
 */
export const downloadFile = async (
  url: string,
  suggestedName?: string,
): Promise<void> => {
  const blob = await fetchBlob(url);
  const baseName = suggestedName || `archivo-${Date.now()}`;
  const hasExt = /\.[a-zA-Z0-9]{2,5}$/.test(baseName);
  const finalName = hasExt ? baseName : `${baseName}.${inferExtension(blob.type, url)}`;
  triggerDownload(blob, finalName);
};

interface ZipItem {
  url: string;
  /** Nombre dentro del ZIP. Si no trae extensión, se infiere del MIME. */
  name?: string;
}

/**
 * Descarga varias URLs y las empaqueta en un ZIP. Concurrencia limitada
 * a 6 fetches en paralelo para no saturar el navegador. Si una falla, se
 * salta y se sigue con el resto (no aborta el ZIP entero).
 *
 * @param items lista de { url, name? } a descargar
 * @param zipName nombre del ZIP final (sin .zip — se agrega solo)
 */
export const downloadFilesAsZip = async (
  items: ZipItem[],
  zipName: string,
): Promise<void> => {
  if (items.length === 0) throw new Error("No hay archivos para descargar");

  // Lazy import — webpack genera un chunk separado para jszip.
  const JSZipMod = await import("jszip");
  const JSZip = JSZipMod.default;
  const zip = new JSZip();
  const seen = new Set<string>();

  // Descarga concurrente con un pool simple de 6 workers.
  const POOL = 6;
  let cursor = 0;
  const fetchNext = async (): Promise<void> => {
    while (cursor < items.length) {
      const idx = cursor++;
      const item = items[idx];
      try {
        const blob = await fetchBlob(item.url);
        const fallback = `archivo-${idx + 1}`;
        const baseName = item.name || fallback;
        const hasExt = /\.[a-zA-Z0-9]{2,5}$/.test(baseName);
        let entryName = sanitizeName(
          hasExt ? baseName : `${baseName}.${inferExtension(blob.type, item.url)}`,
        );
        // Evita colisiones de nombre dentro del ZIP
        if (seen.has(entryName)) {
          const dot = entryName.lastIndexOf(".");
          const stem = dot >= 0 ? entryName.slice(0, dot) : entryName;
          const ext = dot >= 0 ? entryName.slice(dot) : "";
          entryName = `${stem}-${idx + 1}${ext}`;
        }
        seen.add(entryName);
        zip.file(entryName, blob);
      } catch (e) {
        // Saltamos el archivo fallido — el ZIP llega con el resto. NO se registra la URL: es un enlace firmado
        // `/files/<JWT>` (el token va en el path) y la consola del navegador la ven extensiones, soporte remoto
        // y capturas de pantalla. Basta con el nombre y la posición (guard: sinTokenEnConsola.guard.test.ts).
        console.warn(`No se pudo agregar el archivo ${idx + 1} (${item.name || "sin nombre"}) al ZIP`, e);
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(POOL, items.length) }, fetchNext));

  const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
  const finalName = zipName.toLowerCase().endsWith(".zip") ? zipName : `${zipName}.zip`;
  triggerDownload(zipBlob, finalName);
};
